import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';

interface GiftPurchase {
  purchaser_name: string;
  phone: string;
  message: string | null;
  purchased_at: string;
}

interface Gift {
  id: string;
  name: string;
  description: string;
  image_url: string;
  price: number;
  pix_key: string;
  purchase_count: number;
  purchases: GiftPurchase[];
}

interface UseGiftManagementReturn {
  gifts: Gift[];
  loading: boolean;
  error: string | null;
  addGift: (data: any) => Promise<void>;
  editGift: (data: any) => Promise<void>;
  deleteGift: (giftId: string) => Promise<void>;
  refreshGifts: () => Promise<void>;
}

export function useGiftManagement(): UseGiftManagementReturn {
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGifts = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('gifts')
        .select(`
          *,
          purchases:gift_purchases(
            purchaser_name,
            phone,
            message,
            purchased_at
          )
        `)
        .order('name');
        
      if (fetchError) throw fetchError;
      
      const giftsWithCount = data?.map(gift => ({
        ...gift,
        purchase_count: gift.purchases?.length || 0
      })) || [];
      
      setGifts(giftsWithCount);
      setError(null);
    } catch (err) {
      console.error('Error fetching gifts:', err);
      setError('Failed to fetch gifts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGifts();
  }, []);

  const addGift = async (data: any) => {
    try {
      if (!data.imageFile) {
        throw new Error('Image file is required');
      }

      const fileExt = data.imageFile.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `gifts/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('gift_images')
        .upload(filePath, data.imageFile);
        
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from('gift_images')
        .getPublicUrl(filePath);
      
      const { error: insertError } = await supabase
        .from('gifts')
        .insert({
          name: data.name,
          description: data.description,
          price: data.price,
          image_url: publicUrl,
          pix_key: data.pixKey
        });
        
      if (insertError) throw insertError;
      
      await fetchGifts();
      setError(null);
    } catch (err) {
      console.error('Error adding gift:', err);
      throw new Error('Failed to add gift');
    }
  };

  const editGift = async (data: any) => {
    try {
      // Initialize imageUrl with the existing URL from data
      let imageUrl = data.imageUrl;

      // Only upload a new image if a file was provided
      if (data.imageFile) {
        const fileExt = data.imageFile.name.split('.').pop();
        const fileName = `${uuidv4()}.${fileExt}`;
        const filePath = `gifts/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('gift_images')
          .upload(filePath, data.imageFile);
          
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('gift_images')
          .getPublicUrl(filePath);
          
        imageUrl = publicUrl;
      }

      const { error: updateError } = await supabase
        .from('gifts')
        .update({
          name: data.name,
          description: data.description,
          price: data.price,
          image_url: imageUrl,
          pix_key: data.pixKey
        })
        .eq('id', data.id);
        
      if (updateError) throw updateError;
      
      await fetchGifts();
      setError(null);
    } catch (err) {
      console.error('Error editing gift:', err);
      throw new Error('Failed to edit gift');
    }
  };

  const deleteGift = async (giftId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('gifts')
        .delete()
        .eq('id', giftId);
        
      if (deleteError) throw deleteError;
      
      setGifts(gifts.filter(gift => gift.id !== giftId));
      setError(null);
    } catch (err) {
      console.error('Error deleting gift:', err);
      throw new Error('Failed to delete gift');
    }
  };

  return {
    gifts,
    loading,
    error,
    addGift,
    editGift,
    deleteGift,
    refreshGifts: fetchGifts
  };
}